/**
 * Animation engine hook - manages the RequestAnimationFrame loop.
 */

import React, { useEffect, useRef } from 'react'
import type { AnimationAction, AnimationState } from '../types/Animation.types'
import { useAnimationLoop } from './UseAnimationLoop'
import { useVisibilityPause } from './UseVisibilityPause'

type UseAnimationEngineOptions = {
	state: AnimationState
	dispatch: React.Dispatch<AnimationAction>
	onComplete?: () => void
}

/**
 * Hook that manages the animation loop using RequestAnimationFrame.
 * Updates animation state at 60fps when playing.
 */
export function useAnimationEngine({
									   state,
									   dispatch,
									   onComplete,
								   }: UseAnimationEngineOptions) {
	// Create ref to state for use in RAF callback
	const stateRef = useRef(state)
	useEffect(() => {
		stateRef.current = state
	}, [state])

	// Determine if animation loop should be active
	const isActive = state.isPlaying && state.phase === 'execution'

	// Use shared animation loop hook
	useAnimationLoop({
		stateRef,
		dispatch,
		onComplete,
		isActive,
	})

	// Pause animation when tab is hidden
	useVisibilityPause({ stateRef, dispatch })
}

/**
 * Hook to check if animation is currently running.
 */
export function useIsAnimating(state: AnimationState): boolean {
	return state.isPlaying && state.phase === 'execution'
}

/**
 * Hook to get animation progress as percentage.
 */
export function useAnimationProgress(state: AnimationState): number {
	if (state.totalDuration <= 0) return 0
	return Math.min(100, (state.currentTime / state.totalDuration) * 100)
}

/**
 * Hook to get current time formatted as string (e.g., "1.5s").
 */
export function useFormattedTime(state: AnimationState): string {
	const seconds = state.currentTime / 1000
	return `${seconds.toFixed(1)}s`
}

/**
 * Hook to get total duration formatted as string.
 */
export function useFormattedDuration(state: AnimationState): string {
	const seconds = state.totalDuration / 1000
	return `${seconds.toFixed(1)}s`
}
